import { useTranslation } from "react-i18next";
import {
  FormControlLabel as MuiFormControlLabel,
  Radio as MuiRadio,
  Link as MuiLink,
  FormControl as MuiFormControl,
  Grid as MuiGrid,
  TextField as MuiTextField,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Paper as MuiPaper,
  Typography as MuiTypography,
} from "@mui/material";
import { useFormikContext } from "formik";
import { createEditStudyFormStyles } from "app/components/study/edit/CreateEditStudyFormStyles";
import { StudyContactRole } from "app/shared/constants";
import { Theme } from "common";
import { findStudyContactRole } from "app/shared/utils";

const StudyContactsTab = () => {
  const { values, setFieldValue, errors, handleBlur, touched } =
    useFormikContext();

  const { t } = useTranslation();
  const { studyContacts, recruitmentEmail, recruitmentPhone } = values;

  const typoStyle = {
    padding: "5px",
    fontSize: "15px",
  };

  const renderContactRow = (contact, contactIndex) => {
    return (
      <MuiTableRow key={contact.id}>
        <MuiTableCell
          sx={{
            padding: 0,
            borderBottom: "none",
          }}
        >
          <MuiGrid container alignItems="center" size={12}>
            <MuiGrid size={1}>
              {contact.role !== StudyContactRole.PI && (
                <MuiFormControl error={errors.studyContacts ? true : false}>
                  <MuiFormControlLabel
                    sx={{
                      alignItems: "center",
                      margin: 0,
                    }}
                    control={
                      <MuiRadio
                        checked={contact.leadCrc}
                        color="pepGray"
                        onChange={() => {
                          const updatedContacts = values.studyContacts.map(
                            (studyContact, index) => ({
                              ...studyContact,
                              leadCrc: index === contactIndex,
                            }),
                          );
                          setFieldValue("studyContacts", updatedContacts);
                        }}
                        size="small"
                      />
                    }
                    name="leadCrcRadioGroup"
                  />
                </MuiFormControl>
              )}
            </MuiGrid>

            <MuiGrid size={2}>
              <MuiTypography sx={typoStyle}>
                {contact.firstName + " " + contact.lastName}
              </MuiTypography>
            </MuiGrid>

            <MuiGrid size={2.5}>
              <MuiTypography sx={typoStyle}>
                {t(findStudyContactRole(contact.role))}
              </MuiTypography>
            </MuiGrid>

            <MuiGrid size={2}>
              <MuiTypography sx={typoStyle}>{contact.sunetid}</MuiTypography>
            </MuiGrid>

            <MuiGrid size={2}>
              <MuiLink
                href={`mailto:${contact.email}`}
                underline="hover"
                sx={typoStyle}
              >
                {contact.email}
              </MuiLink>
            </MuiGrid>

            <MuiGrid size={2.5}>
              <MuiTextField
                name={`${contact.id}_${contact.role}_note`}
                variant="outlined"
                margin="dense"
                onChange={(event) => {
                  setFieldValue(
                    `studyContacts[${contactIndex}].note`,
                    event.target.value,
                  );
                }}
                value={contact.note}
                size="small"
                sx={{
                  width: "90%",
                }}
              />
            </MuiGrid>
          </MuiGrid>
        </MuiTableCell>
      </MuiTableRow>
    );
  };

  return (
    <MuiGrid container spacing={2} sx={createEditStudyFormStyles.contentNoWrap}>
      <MuiGrid container direction="column" size={2.5}>
        <MuiGrid sx={createEditStudyFormStyles.customContactGridWidth}>
          <MuiTextField
            fullWidth
            name="recruitmentEmail"
            label={t(`formLabel.recruitmentEmail`)}
            value={recruitmentEmail}
            variant="outlined"
            margin="dense"
            onBlur={handleBlur}
            size="small"
            onChange={(e) => {
              setFieldValue("recruitmentEmail", e.target.value);
            }}
            error={
              touched.recruitmentEmail && errors.recruitmentEmail ? true : false
            }
            helperText={
              touched.recruitmentEmail &&
              errors.recruitmentEmail &&
              t("formValidation.invalidEmail")
            }
          />
        </MuiGrid>
        <MuiGrid sx={createEditStudyFormStyles.customContactGridWidth}>
          <MuiTextField
            fullWidth
            name="recruitmentPhone"
            label={t(`formLabel.recruitmentPhone`)}
            value={recruitmentPhone}
            variant="outlined"
            margin="dense"
            size="small"
            onBlur={handleBlur}
            onChange={(e) => {
              setFieldValue("recruitmentPhone", e.target.value);
            }}
            error={
              touched.recruitmentPhone && errors.recruitmentPhone ? true : false
            }
            helperText={
              touched.recruitmentPhone &&
              errors.recruitmentPhone &&
              t("formValidation.invalidPhoneNumber")
            }
          />
        </MuiGrid>
      </MuiGrid>
      <MuiGrid display="flex" flexDirection="column" size={12}>
        <MuiGrid>
          <MuiTableContainer component={MuiPaper}>
            <MuiTable>
              <MuiTableHead>
                <MuiTableRow
                  sx={{
                    backgroundColor: Theme.palette.grey[700],
                    color: Theme.palette.common.white,
                  }}
                >
                  <MuiTableCell sx={{ color: Theme.palette.common.white }}>
                    <MuiGrid container size={12}>
                      <MuiGrid size={1}>{t("formLabel.leadCRC")}</MuiGrid>
                      <MuiGrid size={2}>{t(`formLabel.name`)}</MuiGrid>
                      <MuiGrid size={2.5}>{t(`formLabel.role`)}</MuiGrid>
                      <MuiGrid size={2}>{t(`formLabel.sunetid`)}</MuiGrid>
                      <MuiGrid size={2}>{t(`formLabel.email`)}</MuiGrid>
                      <MuiGrid size={2.5}>{t(`formLabel.note`)}</MuiGrid>
                    </MuiGrid>
                  </MuiTableCell>
                </MuiTableRow>
              </MuiTableHead>
              <MuiTableBody>
                {/* PI */}
                {studyContacts &&
                  studyContacts.map((contact, index) => {
                    return (
                      contact.role === StudyContactRole.PI &&
                      renderContactRow(contact, index)
                    );
                  })}

                {/* PRIMARY_CRC */}
                {studyContacts &&
                  studyContacts.map((contact, index) => {
                    return (
                      contact.role !== StudyContactRole.PI &&
                      renderContactRow(contact, index)
                    );
                  })}
              </MuiTableBody>
            </MuiTable>
          </MuiTableContainer>
        </MuiGrid>
      </MuiGrid>
    </MuiGrid>
  );
};

export default StudyContactsTab;
